import React from 'react';
import { FeedQuantityEstimation } from '../api/nutrition';

interface FeedQuantityEstimationProps {
  data: FeedQuantityEstimation;
  className?: string;
}

const FeedQuantityEstimationComponent: React.FC<FeedQuantityEstimationProps> = ({ 
  data, 
  className = '' 
}) => {
  const {
    estimatedDailyFeedKg,
    totalFlockWeightKg,
    birdCount,
    calculationMethod,
    weightBreakdown
  } = data;

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <div className="flex items-center mb-4">
        <div className="bg-blue-100 p-2 rounded-lg mr-3">
          <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Feed Quantity Estimation</h3>
          <p className="text-sm text-slate-600">{calculationMethod}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center">
            <div className="bg-green-100 p-2 rounded-full mr-3">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-green-800">Daily Feed Required</p>
              <p className="text-2xl font-bold text-green-900">{estimatedDailyFeedKg.toFixed(1)} kg</p>
            </div>
          </div>
        </div>

        <div className="bg-cyan-50 p-4 rounded-lg">
          <div className="flex items-center">
            <div className="bg-blue-100 p-2 rounded-full mr-3">
              <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-cyan-800">Total Birds</p>
              <p className="text-2xl font-bold text-blue-900">{birdCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex items-center">
            <div className="bg-purple-100 p-2 rounded-full mr-3">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-purple-800">Total Flock Weight</p>
              <p className="text-2xl font-bold text-purple-900">{totalFlockWeightKg.toFixed(1)} kg</p>
            </div>
          </div>
        </div>
      </div>

      {weightBreakdown.length > 0 && (
        <div>
          <h4 className="text-md font-semibold text-gray-800 mb-3">Feed Requirements by Species</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Species</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Count</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Total Weight</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Daily Feed</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">% of Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {weightBreakdown.map((species, index) => {
                  const percentage = (species.feedContributionKg / estimatedDailyFeedKg) * 100;
                  return (
                    <tr key={index} className="hover:bg-slate-50/80">
                      <td className="px-4 py-2 text-sm font-medium text-slate-900">
                        {species.speciesName}
                      </td>
                      <td className="px-4 py-2 text-sm text-slate-600">
                        {species.count}
                      </td>
                      <td className="px-4 py-2 text-sm text-slate-600">
                        {species.totalWeightKg.toFixed(1)} kg
                      </td>
                      <td className="px-4 py-2 text-sm text-slate-600">
                        {species.feedContributionKg.toFixed(1)} kg
                      </td>
                      <td className="px-4 py-2 text-sm text-slate-600">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className="bg-cyan-600 h-2 rounded-full" 
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          {percentage.toFixed(1)}%
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {birdCount === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-yellow-800">No Active Birds Found</p>
              <p className="text-xs text-yellow-700">Please check bird status data to calculate feed requirements.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedQuantityEstimationComponent; 